// Utility functions for Bilbase Danmark

/**
 * Format price in Danish Kroner
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Format number with Danish locale
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('da-DK').format(num);
}

/**
 * Clamp en dato så vi aldrig viser en dato i fremtiden.
 * Bruges på alle udgivelses-/oprettelses-datoer som er user-facing,
 * så fejlagtige fremtidige datoer (fx skemalagte poster) ikke
 * undergraver tilliden til indholdet.
 */
export function safeDisplayDate(date: string | Date): Date {
  const d = new Date(date);
  const now = new Date();
  return d.getTime() > now.getTime() ? now : d;
}

/**
 * Format date in Danish format
 */
export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(safeDisplayDate(date));
}

/**
 * Format date in short format
 */
export function formatDateShort(date: string | Date): string {
  return new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(safeDisplayDate(date));
}

/**
 * Generate a URL-safe slug from a string
 */
export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[æ]/g, 'ae')
    .replace(/[ø]/g, 'o')
    .replace(/[å]/g, 'a')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Truncate text to a max length with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

/**
 * Get the powertrain badge variant
 */
export function getPowertrainVariant(type: string): 'ice' | 'hybrid' | 'phev' | 'ev' {
  switch (type) {
    case 'ICE': return 'ice';
    case 'Hybrid': return 'hybrid';
    case 'PHEV': return 'phev';
    case 'EV': return 'ev';
    default: return 'ice';
  }
}

/**
 * Get a relative time description
 */
export function getRelativeTime(date: string | Date): string {
  const now = new Date();
  const then = safeDisplayDate(date);
  const diffInMs = Math.max(0, now.getTime() - then.getTime());
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'I dag';
  if (diffInDays === 1) return 'I går';
  if (diffInDays < 7) return `${diffInDays} dage siden`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} uger siden`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} måneder siden`;
  return `${Math.floor(diffInDays / 365)} år siden`;
}
